import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion'; // eslint-disable-line no-unused-vars
import { Bell, HelpCircle, X } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationList } from './NotificationList';
import { SeverityFilter } from './SeverityFilter';
import { DesktopAlertsToggle } from './DesktopAlertsToggle';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { destinationPath } from '../../utils/notificationRoutes';

/**
 * Notifications panel — a right-side slide-in drawer. Owns:
 *   - severity filter chip state ('all' | 'unread' | 'action')
 *   - keyboard-navigation focus index over the filtered list
 *   - keyboard-shortcuts help modal open state
 *
 * Rendered through a portal to <body> so the header's backdrop-filter (a
 * containing block for fixed descendants) can't trap the drawer. Delegates list
 * rendering (loading/error/empty/grouping) to NotificationList.
 */
export function NotificationDropdown({ open = true, onClose }) {
  const { recent, markAllRead, markRead, softDelete, refresh, loading, error } = useNotifications();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const [filter, setFilter] = useState('all');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  // Pure, non-mutating filter + counts.
  const counts = useMemo(() => ({
    all: recent.length,
    unread: recent.filter((n) => n.readAt == null).length,
    action: recent.filter((n) => n.severity === 'ACTION_REQUIRED').length,
  }), [recent]);

  const items = useMemo(() => applyFilter(recent, filter), [recent, filter]);

  // Clamp focused index when the visible set shrinks (e.g. filter change).
  const safeFocused = items.length === 0 ? null : Math.min(focusedIndex, items.length - 1);

  // Keyboard shortcuts — only active while the drawer is open AND the help
  // modal is closed (so j/k inside the modal can't fight us).
  useKeyboardShortcuts({
    j: () => setFocusedIndex((i) => Math.min((items.length || 1) - 1, (i ?? -1) + 1)),
    k: () => setFocusedIndex((i) => Math.max(0, (i ?? 1) - 1)),
    e: () => {
      const n = items[safeFocused ?? 0];
      if (n && n.readAt == null) markRead(n.id);
    },
    Enter: () => {
      const n = items[safeFocused ?? 0];
      if (!n) return;
      if (n.readAt == null) markRead(n.id);
      const path = destinationPath(n.entityType, n.entityId);
      if (path) navigate(path);
      onClose?.();
    },
    Escape: () => onClose?.(),
    Delete: () => {
      const n = items[safeFocused ?? 0];
      if (n) softDelete(n.id);
    },
    Backspace: () => {
      const n = items[safeFocused ?? 0];
      if (n) softDelete(n.id);
    },
    '?': () => setHelpOpen(true),
  }, { enabled: open && !helpOpen });

  const hasUnread = counts.unread > 0;
  const emptyVariant = filter === 'unread' ? 'unread' : filter === 'action' ? 'action' : 'default';

  const panelMotion = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' } };

  return createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Drawer */}
      <motion.aside
        {...panelMotion}
        transition={reduce ? { duration: 0.15 } : { duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Notifications"
        className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950"
      >
        {/* Title row */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3.5 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-9 w-9 items-center justify-center bg-primary-50 text-primary-600 dark:bg-carbon-400/10 dark:text-carbon-400">
              <Bell className="h-5 w-5" strokeWidth={1.75} />
              {hasUnread && (
                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold leading-none text-white ring-2 ring-white dark:ring-slate-950">
                  {counts.unread > 99 ? '99+' : counts.unread}
                </span>
              )}
            </span>
            <div className="leading-tight">
              <h2 className="font-serif text-lg font-bold text-slate-900 dark:text-white">Notifications</h2>
              <p className="font-ledger-mono text-xs text-slate-500 dark:text-slate-400">
                {hasUnread ? `${counts.unread} unread` : 'All caught up'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notifications"
            className="inline-flex h-8 w-8 items-center justify-center text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:focus-visible:ring-carbon-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Secondary actions */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2 text-xs dark:border-slate-800/60">
          {hasUnread ? (
            <button
              type="button"
              onClick={() => markAllRead()}
              className="font-medium text-primary-600 hover:underline dark:text-carbon-400"
            >
              Mark all as read
            </button>
          ) : <span />}
          <div className="flex items-center gap-3">
            <Link to="/notifications" onClick={onClose} className="font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200">
              View all
            </Link>
            <Link to="/settings/notifications" onClick={onClose} className="font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200">
              Settings
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2 dark:border-slate-800/60">
          <SeverityFilter value={filter} onChange={setFilter} counts={counts} size="sm" />
          <DesktopAlertsToggle />
        </div>

        {/* List — the only scroller */}
        <div className="flex-1 overflow-y-auto py-1">
          <NotificationList
            items={items}
            loading={loading}
            error={error}
            onRetry={refresh}
            compact
            onRowClick={onClose}
            emptyVariant={emptyVariant}
            focusedIndex={safeFocused}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-slate-200 px-4 py-2 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
            aria-label="Show keyboard shortcuts"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            Shortcuts
          </button>
        </div>
      </motion.aside>

      <KeyboardShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>,
    document.body
  );
}

function applyFilter(items, filter) {
  switch (filter) {
    case 'unread':
      return items.filter((n) => n.readAt == null);
    case 'action':
      return items.filter((n) => n.severity === 'ACTION_REQUIRED');
    case 'all':
    default:
      return items.slice();
  }
}
